'use client';

import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { usePhoneInput } from 'react-international-phone';
import { getCountryCallingCode, type CountryCode } from 'libphonenumber-js';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input/input';
import { Flag } from '@/components/ui/flag/flag';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command/command';

/**
 * UK pseudo-country codes used by rides in England, Scotland, Wales, Northern Ireland.
 * Libraries don't recognise these — map them to GB for phone parsing.
 */
const UK_NATION_CODES = new Set(['XE', 'XS', 'XW', 'XI']);

function toPhoneCountryCode(code: string): string {
  return UK_NATION_CODES.has(code) ? 'GB' : code;
}

export type PhoneInputVariant = 'dark' | 'light';
export type PhoneInputSize = 'md' | 'lg';

/**
 * Per-variant colour classes. Height lives in `SIZE_HEIGHT` so a variant can be
 * sized independently (e.g. the light picker at h-12 inside a registration form).
 *
 * - `dark`: the literal stone/dgr-gold palette used by the DGR/DGD public pages.
 * - `light`: shadcn semantic tokens — themed by the surrounding `[data-tenant]`
 *   CSS scope (e.g. UWR resolves `primary` to orange).
 */
const VARIANT_CLASSES: Record<PhoneInputVariant, Record<string, string>> = {
  dark: {
    trigger: 'bg-stone-800 border-stone-700 text-white hover:bg-stone-700 hover:text-white',
    dialCode: 'text-stone-400',
    popover: 'bg-stone-900 border-stone-700',
    command: 'bg-transparent',
    commandInput: 'text-white placeholder:text-stone-500',
    commandEmpty: 'text-stone-400',
    commandItem: 'text-white data-[selected=true]:bg-stone-800 data-[selected=true]:text-white',
    countryDial: 'text-stone-500',
    check: 'text-dgr-gold',
    input: 'bg-stone-900 border-stone-700 text-white placeholder:text-stone-500',
  },
  light: {
    trigger:
      'bg-background border-input text-foreground hover:bg-accent hover:text-accent-foreground',
    dialCode: 'text-muted-foreground',
    popover: '',
    command: '',
    commandInput: '',
    commandEmpty: '',
    commandItem: '',
    countryDial: 'text-muted-foreground',
    check: 'text-primary',
    input: '',
  },
};

const SIZE_HEIGHT: Record<PhoneInputSize, string> = {
  md: 'h-10',
  lg: 'h-12',
};

/**
 * Country data from useCountries() hook
 */
export interface Country {
  id: string;
  code: string;
  code3?: string | null;
  name: string;
  phoneCode?: string | null;
}

export interface PhoneInputProps {
  /** Phone number in E.164 format (e.g., "+61412345678") */
  value?: string;
  /** Called when phone number changes (E.164 format) */
  onChange?: (value: string) => void;
  /** Called when input loses focus */
  onBlur?: () => void;
  /** Default country code (ISO 3166-1 alpha-2, e.g., "AU") */
  defaultCountry?: string;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Disable the input */
  disabled?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Additional className for the popover content (useful for dark themes) */
  popoverClassName?: string;
  /** Input id for accessibility */
  id?: string;
  /** Countries data from useCountries() hook */
  countries: Country[];
  /** Visual variant: 'dark' for public pages, 'light' for admin dashboard / light tenants */
  variant?: PhoneInputVariant;
  /** Control height independently of variant. Defaults to lg (h-12) for dark, md (h-10) for light. */
  size?: PhoneInputSize;
}

/**
 * PhoneInput - International phone number input with country selector
 *
 * Uses react-international-phone's headless hook for phone logic,
 * with our own shadcn/ui Popover + Command for the country selector.
 *
 * @example
 * ```tsx
 * const { data: countries = [] } = useCountries();
 *
 * <PhoneInput
 *   value={phone}
 *   onChange={setPhone}
 *   defaultCountry="AU"
 *   countries={countries}
 * />
 * ```
 */
const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value = '',
      onChange,
      onBlur,
      defaultCountry = 'AU',
      placeholder,
      disabled = false,
      className,
      popoverClassName,
      id,
      countries,
      variant = 'dark',
      size,
    },
    _ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    // Track the display country separately for UK nation codes
    const [displayCountry, setDisplayCountry] = React.useState<string>(defaultCountry);

    const { inputValue, country, setCountry, handlePhoneValueChange, inputRef } = usePhoneInput({
      defaultCountry: toPhoneCountryCode(defaultCountry).toLowerCase(),
      value,
      disableCountryGuess: true,
      disableDialCodeAndPrefix: true,
      allowMaskOverflow: true,
      onChange: ({ phone: newPhone }) => {
        // Only emit non-empty values (hook emits "+" for empty input)
        onChange?.(newPhone.length > 1 ? newPhone : '');
      },
    });

    // Sync displayCountry when the hook's country changes (e.g. from prefill)
    React.useEffect(() => {
      const hookIso = country.iso2.toUpperCase();
      // Don't overwrite a UK nation code with GB
      if (hookIso === 'GB' && UK_NATION_CODES.has(displayCountry)) return;
      if (hookIso !== displayCountry) {
        setDisplayCountry(hookIso);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country.iso2]);

    // Get dial code for display
    const dialCode = React.useMemo(() => {
      try {
        return `+${getCountryCallingCode(toPhoneCountryCode(displayCountry) as CountryCode)}`;
      } catch {
        const c = countries.find((c) => c.code === displayCountry);
        return c?.phoneCode || '+1';
      }
    }, [displayCountry, countries]);

    // Handle country selection from our Popover
    const handleCountrySelect = (countryCode: string) => {
      setDisplayCountry(countryCode);
      setOpen(false);
      setCountry(toPhoneCountryCode(countryCode).toLowerCase());
    };

    const v = VARIANT_CLASSES[variant];
    // Dark defaulted to h-12, light to h-10 — preserve that unless overridden.
    const height = SIZE_HEIGHT[size ?? (variant === 'dark' ? 'lg' : 'md')];

    return (
      <div className={cn('flex', className)}>
        {/* Country Selector */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-label="Select country"
              disabled={disabled}
              className={cn(
                'w-[100px] justify-between rounded-r-none border-r-0 px-3',
                height,
                v.trigger,
              )}
            >
              <span className="flex items-center gap-2">
                <Flag code={displayCountry} size="sm" />
                <span className={cn('text-sm', v.dialCode)}>{dialCode}</span>
              </span>
              <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn('w-[300px] p-0', v.popover, popoverClassName)}
            align="start"
            side="bottom"
          >
            <Command className={v.command}>
              <CommandInput
                placeholder="Search country..."
                className={cn('h-10', v.commandInput)}
              />
              <CommandList>
                <CommandEmpty className={v.commandEmpty}>No country found.</CommandEmpty>
                <CommandGroup>
                  {countries.map((c) => (
                    <CommandItem
                      key={c.code}
                      value={`${c.name} ${c.code}`}
                      onSelect={() => handleCountrySelect(c.code)}
                      className={cn('cursor-pointer', v.commandItem)}
                    >
                      <Flag code={c.code} size="sm" className="mr-2" />
                      <span className="flex-1">{c.name}</span>
                      <span className={cn('text-sm', v.countryDial)}>
                        {c.phoneCode ||
                          `+${(() => {
                            try {
                              return getCountryCallingCode(c.code as CountryCode);
                            } catch {
                              return '?';
                            }
                          })()}`}
                      </span>
                      {displayCountry === c.code && (
                        <Check className={cn('ml-2 h-4 w-4', v.check)} />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Phone Number Input */}
        <Input
          ref={inputRef}
          id={id}
          type="tel"
          value={inputValue}
          onChange={handlePhoneValueChange}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder || 'Phone number'}
          className={cn('rounded-l-none flex-1', height, v.input)}
          autoComplete="tel-national"
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
        />
      </div>
    );
  },
);
PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };
